import type { AccountView, CredentialView } from "../api/types.js";
import { statementDate } from "../lib/format.js";

/**
 * 19 Security (DCTA-111 run 2; UX spec §E.4/§E.6, mock-up 19-security.html), from what the lab bank
 * serves: each account's `locked` (`GET /v1/accounts`) and the customer's passkeys
 * (`GET /v1/webauthn/credentials`: created_at and the BE/BS flags). The mock-up's other blocks —
 * signed-in devices, recent sign-ins, the security token's state, removing a passkey — have no read
 * or action in the lab yet (backend asks ASK-17, ASK-18) and are not drawn: this screen prints no
 * fact the bank did not give.
 *
 * Copy is the deck's (§E.6) where the deck has it; where the deck's line needs a fact the bank does
 * not serve ("Last sign-in today, 9:40 pm, on this iPhone"; "Since 9:42 pm"), the line says what
 * IS known — flagged for ux-designer. Never "device-bound" (CONTEXT.md claims discipline).
 */
export type { CredentialView } from "../api/types.js";

export interface SecurityModel {
  /** `lock_funds` locks every account at once: any locked account is the lock. */
  readonly locked: boolean;
  readonly status: { readonly title: string; readonly sub: string };
  readonly passkeys: readonly { readonly title: string; readonly sub: string }[];
}

const SYNCED = "synced across your devices";
/** The lock's status — what stops (Umakhan, 14 Oct: never "nothing … until …", test/no-slogan.spec.ts). */
export const LOCKED_STATUS = { title: "Your money is locked", sub: "Payments are stopped" } as const;
/**
 * DCTA-231 V2 (the coordinator, 15 Oct): Home's lock banner says the bank's own lock sentence (lab/dcta policy/refusal-words.ts
 * `LOCKED_RESULT`, "Your money is locked. Payments, transfers and investments are stopped."), as the titled banner draws it.
 */
export const LOCKED_HOME = { title: LOCKED_STATUS.title, line: "Payments, transfers and investments are stopped." } as const;
/**
 * DCTA-232 F1 (prod-verify 15 Oct): the way out of the lock is the bank's own unlock, asked for in the conversation in the
 * customer's words — never a one-tap unlock and never a form. The bank builds the card from these words (Check and sign,
 * the passkey, a token code, the 12-hour hold); they are the phrase its deterministic unlock knows (backend-architect, the
 * same task), so the row on Security says exactly what it sends.
 */
export const UNLOCK_WORDS = "Unlock my money";
/** What unlocking takes — the bank's own sentence (policy-config `protection.funds_lock.unlock_message`, the lock refusal's). */
export const UNLOCK_TAKES = "Unlocking takes your passkey, a token code and 12 hours.";
/** DCTA-232: the bank's unlock card — its title (deck §E.6, mock-up 19) and its sign button (the coordinator, 15 Oct). */
export const UNLOCK_TITLE = "Unlock your money";
export const UNLOCK_SIGN = "Sign to unlock";

export function securityModel(input: { readonly accounts: readonly AccountView[]; readonly credentials: readonly CredentialView[] }): SecurityModel {
  const locked = input.accounts.some((a) => a.locked);
  const n = input.credentials.length;
  const allSynced = n > 0 && input.credentials.every((c) => c.backed_up);
  const status = locked
    ? LOCKED_STATUS
    : n === 0
      ? { title: "No passkey yet", sub: "One is made the first time you sign" }
      : { title: "Protected by your passkey", sub: `${String(n)} passkey${n === 1 ? "" : "s"}${allSynced ? ` · ${SYNCED}` : ""}` };
  const passkeys = [...input.credentials]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((c) => ({ title: "Passkey", sub: `Saved ${statementDate(c.created_at)}${c.backed_up ? ` · ${SYNCED}` : ""}` }));
  return { locked, status, passkeys };
}
